import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { publicPost } from "../../../services/apiClient";
import { API_ENDPOINTS } from "../../../config/apiEndpoints";
import {
  parseResponseJson,
  parseSchemaJson,
} from "./esourceService";
import {
  applyFieldDefaults,
  collectRequiredMissing,
  collectFieldValidationIssues,
  evaluateLogicVisibility,
} from "./esourceFieldTypes";
import { ESourceFormFields } from "./esourceFormRender";
import "./ESourcePages.scss";

async function guestPost(url, body) {
  const response = await publicPost(url, body);
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const message = response?.message || response?.Message || "";
  const ds = response?.data ?? response?.Data ?? null;
  let row = null;
  if (ds && typeof ds === "object") {
    const tables = Object.keys(ds).filter((k) => Array.isArray(ds[k]));
    if (tables.length) row = ds[tables[0]][0] || null;
  }
  return { success, message, row, response };
}

const ESourcePublicFill = () => {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("t") || "").trim();

  const [loading, setLoading] = useState(true);
  const [instance, setInstance] = useState(null);
  const [values, setValues] = useState({});
  const [actorEmail, setActorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setMessage("This form link is missing or incomplete.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const res = await guestPost(API_ENDPOINTS.ESOURCE.PUBLIC_GET, {
        AccessToken: token,
      });
      if (!res.success || !res.row) {
        setInstance(null);
        setMessage(res.message || "This form link is invalid or expired.");
        return;
      }
      setInstance(res.row);
      const schema = parseSchemaJson(res.row.SchemaJson);
      setValues(
        applyFieldDefaults(schema.fields, parseResponseJson(res.row.ResponseJson)),
      );
      if (res.row.RecipientEmail) setActorEmail(String(res.row.RecipientEmail));
    } catch (err) {
      setInstance(null);
      setMessage(err?.message || "Could not open this form.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const fields = useMemo(
    () => parseSchemaJson(instance?.SchemaJson).fields,
    [instance],
  );
  const schemaExtras = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      logic: schema.logic || [],
      layout: schema.settings?.layout || "two-column",
    };
  }, [instance]);
  const formIntro = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      header: schema.formHeader || "",
      description: schema.formDescription || "",
    };
  }, [instance]);

  const setFieldValue = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const responseJson = JSON.stringify(values);

  const onSaveDraft = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await guestPost(API_ENDPOINTS.ESOURCE.PUBLIC_SAVE, {
        AccessToken: token,
        ResponseJson: responseJson,
        ActorEmail: actorEmail || null,
        Title: instance?.Title,
      });
      setMessage(res.message || (res.success ? "Draft saved." : "Save failed."));
    } catch (err) {
      setMessage(err?.message || "Could not save draft.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    const { visible } = evaluateLogicVisibility(
      fields,
      values,
      schemaExtras.logic,
    );
    const missing = collectRequiredMissing(fields, values, visible);
    if (missing.length) {
      setMessage(
        `Please complete required fields: ${missing.map((f) => f.label).join(", ")}`,
      );
      return;
    }
    const issues = collectFieldValidationIssues(fields, values, visible);
    if (issues.length) {
      setMessage(issues.map((i) => i.message).join(" "));
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const res = await guestPost(API_ENDPOINTS.ESOURCE.PUBLIC_SUBMIT, {
        AccessToken: token,
        ResponseJson: responseJson,
        ActorEmail: actorEmail || null,
        Title: instance?.Title,
      });
      setMessage(res.message || "");
      if (res.success) setSubmitted(true);
    } catch (err) {
      setMessage(err?.message || "Could not submit the form.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="esource-public-fill">
      <header className="esource-public-fill__brand">
        <span className="esource-public-fill__logo">ascensionQ</span>
        <span className="esource-public-fill__module">e-Source</span>
      </header>

      <main className="esource-public-fill__main">
        {loading ? (
          <p className="text-muted mb-0">Loading form…</p>
        ) : submitted ? (
          <div className="esource-public-fill__done">
            <h1>Thank you</h1>
            <p>{message || "Your responses have been submitted."}</p>
          </div>
        ) : !instance ? (
          <div className="esource-public-fill__done">
            <h1>Unable to open form</h1>
            <p>{message || "This link is invalid or has expired."}</p>
          </div>
        ) : (
          <>
            <div className="esource-public-fill__head">
              <h1>{instance.Title || instance.TemplateName || "Form"}</h1>
              <p>
                {instance.TemplateCode}
                {instance.VersionNo != null ? ` · v${instance.VersionNo}` : ""}
              </p>
            </div>

            {message ? <div className="esource-alert">{message}</div> : null}

            <div className="esource-card">
              {formIntro.header || formIntro.description ? (
                <div className="esource-form-intro-block">
                  {formIntro.header ? (
                    <h2 className="esource-form-intro-block__header">
                      {formIntro.header}
                    </h2>
                  ) : null}
                  {formIntro.description ? (
                    <p className="esource-form-intro-block__description">
                      {formIntro.description}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <label className="form-label">Your email (optional)</label>
              <input
                className="form-control mb-3"
                type="email"
                value={actorEmail}
                disabled={busy}
                onChange={(e) => setActorEmail(e.target.value)}
                placeholder="Used only for this submission audit"
              />

              <ESourceFormFields
                fields={fields}
                values={values}
                disabled={busy || submitted}
                onChange={setFieldValue}
                emptyText="This form has no fields."
                logic={schemaExtras.logic}
                layout={schemaExtras.layout}
              />
            </div>

            <div className="esource-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={busy}
                onClick={onSaveDraft}
              >
                Save draft
              </button>
              <button
                type="button"
                className="btn btn-esource"
                disabled={busy}
                onClick={onSubmit}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </main>

      <footer className="esource-public-fill__footer">
        © {new Date().getFullYear()} Ascension-q
      </footer>
    </div>
  );
};

export default ESourcePublicFill;
